import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import { compressImageFile } from '@/lib/imageCompress';

interface ZTicket {
  id: number;
  user_id: number;
  user_name: string | null;
  user_email: string | null;
  subject: string;
  category: string;
  message: string | null;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface ZMsg {
  id: number;
  ticket_id: number;
  sender_type: string;
  sender_name: string | null;
  message: string;
  attachment_url: string | null;
  created_at: string;
}

/** Compresse puis envoie une capture d'écran comme pièce jointe de ticket. */
async function uploadAttachment(file: File): Promise<string | null> {
  try {
    const compressed = await compressImageFile(file);
    return await uploadMediaFile(compressed, 'support');
  } catch {
    return null;
  }
}

export const ZIFEL_SUPPORT_STATUS: Record<string, { label: string; className: string }> = {
  open: { label: 'Ouvert', className: 'bg-accent-50 text-accent-700' },
  in_progress: { label: 'En cours', className: 'bg-primary-50 text-primary-700' },
  resolved: { label: 'Résolu', className: 'bg-secondary-100 text-secondary-700' },
};

const CATEGORIES = [
  { value: 'general', label: 'Question générale' },
  { value: 'bug', label: 'Signaler un problème' },
  { value: 'feedback', label: 'Retour / suggestion' },
  { value: 'billing', label: 'Abonnement & paiement' },
  { value: 'credit', label: 'Zifek Crédit' },
];

function fmt(d: string) {
  return new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/**
 * ZifekSupportPanel — permet au commerçant d'échanger avec l'équipe Zifek
 * (retour, signalement de problème). Stocké dans zifek_tickets / zifek_ticket_messages.
 */
export default function ZifekSupportPanel() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<ZTicket[]>([]);
  const [messages, setMessages] = useState<Record<number, ZMsg[]>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', message: '' });
  const [creating, setCreating] = useState(false);
  const [replyAttach, setReplyAttach] = useState<{ file: File; preview: string } | null>(null);
  const [formAttach, setFormAttach] = useState<{ file: File; preview: string } | null>(null);
  const replyFileRef = useRef<HTMLInputElement>(null);
  const formFileRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: tErr } = await supabase
        .from('zifek_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      if (tErr) throw tErr;
      const list = (data || []) as ZTicket[];
      setTickets(list);

      const ids = list.map((t) => t.id);
      const grouped: Record<number, ZMsg[]> = {};
      if (ids.length > 0) {
        const { data: msgData, error: mErr } = await supabase
          .from('zifek_ticket_messages')
          .select('*')
          .in('ticket_id', ids)
          .order('created_at', { ascending: true });
        if (mErr) throw mErr;
        (msgData || []).forEach((m) => {
          const msg = m as ZMsg;
          if (!grouped[msg.ticket_id]) grouped[msg.ticket_id] = [];
          grouped[msg.ticket_id].push(msg);
        });
      }
      setMessages(grouped);
      setSelectedId((prev) => (prev && list.some((t) => t.id === prev) ? prev : list[0]?.id ?? null));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [selectedId, messages]);

  const createTicket = async () => {
    if (!user || !form.subject.trim() || !form.message.trim()) return;
    setCreating(true);
    setError(null);
    try {
      let attachmentUrl: string | null = null;
      if (formAttach) attachmentUrl = await uploadAttachment(formAttach.file);
      const { data: created, error: cErr } = await supabase
        .from('zifek_tickets')
        .insert({
          user_id: user.id,
          user_name: user.name || user.nomcommerce || '',
          user_email: user.email || '',
          subject: form.subject.trim(),
          category: form.category,
          message: form.message.trim(),
          status: 'open',
        })
        .select('id')
        .single();
      if (cErr || !created) throw cErr || new Error('Création impossible');
      const ticketId = created.id as number;
      await supabase.from('zifek_ticket_messages').insert({
        ticket_id: ticketId,
        sender_type: 'merchant',
        sender_name: user.name || user.nomcommerce || 'Commerçant',
        message: form.message.trim(),
        attachment_url: attachmentUrl,
      });
      setForm({ subject: '', category: 'general', message: '' });
      setFormAttach(null);
      setShowForm(false);
      await load();
      setSelectedId(ticketId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const sendReply = async () => {
    if (!selectedId || !user) return;
    if (!text.trim() && !replyAttach) return;
    setSending(true);
    try {
      let attachmentUrl: string | null = null;
      if (replyAttach) attachmentUrl = await uploadAttachment(replyAttach.file);
      const { error: err } = await supabase.from('zifek_ticket_messages').insert({
        ticket_id: selectedId,
        sender_type: 'merchant',
        sender_name: user.name || user.nomcommerce || 'Commerçant',
        message: text.trim(),
        attachment_url: attachmentUrl,
      });
      if (err) throw err;
      await supabase.from('zifek_tickets').update({ updated_at: new Date().toISOString(), status: 'open' }).eq('id', selectedId);
      setText('');
      setReplyAttach(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur lors de l\u2019envoi');
    } finally {
      setSending(false);
    }
  };

  const selected = tickets.find((t) => t.id === selectedId) || null;
  const thread = selected ? messages[selected.id] || [] : [];

  return (
    <div className="space-y-4">
      {/* Intro + new ticket */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
              <i className="ri-customer-service-2-line text-xl"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground-950">Contacter l&apos;équipe Zifek</h3>
              <p className="text-xs text-foreground-500 mt-0.5 max-w-lg">
                Un retour, une suggestion ou un problème ? Écrivez-nous : notre équipe vous répond directement ici.
              </p>
            </div>
          </div>
          <button onClick={() => setShowForm((v) => !v)}
            className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors self-start sm:self-auto">
            <i className={`ri-${showForm ? 'close' : 'add'}-line`}></i>
            {showForm ? 'Fermer' : 'Écrire à Zifek'}
          </button>
        </div>

        {showForm && (
          <div className="mt-4 pt-4 border-t border-background-200/70 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Sujet</label>
              <input type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="Ex : Problème de synchronisation"
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Catégorie</label>
              <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1">Message</label>
              <textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                rows={3} maxLength={1000} placeholder="Décrivez votre demande…"
                className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 resize-none" />
            </div>
            {formAttach && (
              <div className="sm:col-span-2 flex items-center gap-2">
                <img src={formAttach.preview} alt="Aperçu" className="w-16 h-16 rounded-lg object-cover border border-background-200/70" />
                <button type="button" onClick={() => setFormAttach(null)} className="text-xs text-foreground-500 hover:text-foreground-800 cursor-pointer whitespace-nowrap">Retirer</button>
              </div>
            )}
            <div className="sm:col-span-2 flex items-center gap-2">
              <input ref={formFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setFormAttach({ file, preview: URL.createObjectURL(file) });
                if (formFileRef.current) formFileRef.current.value = '';
              }} />
              <button type="button" onClick={() => formFileRef.current?.click()}
                className="flex items-center gap-1.5 px-3.5 py-2.5 rounded-full bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-image-add-line"></i> Joindre une capture
              </button>
              <button onClick={createTicket} disabled={creating || !form.subject.trim() || !form.message.trim()}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors whitespace-nowrap">
                {creating ? 'Envoi…' : 'Envoyer à Zifek'}
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i><span className="flex-1">{error}</span>
          <button onClick={load} className="underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-message-3-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucune demande pour le moment.</p>
          <p className="text-xs text-foreground-400 mt-1">Cliquez sur « Écrire à Zifek » pour démarrer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)] gap-4 items-start">
          {/* Liste */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden divide-y divide-background-200/70">
            {tickets.map((t) => {
              const meta = ZIFEL_SUPPORT_STATUS[t.status] || ZIFEL_SUPPORT_STATUS.open;
              return (
                <button key={t.id} onClick={() => setSelectedId(t.id)}
                  className={`w-full text-left px-4 py-3 transition-colors cursor-pointer ${t.id === selectedId ? 'bg-primary-50' : 'hover:bg-background-50'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground-900 truncate">{t.subject}</span>
                    <span className="text-[11px] text-foreground-400 whitespace-nowrap">{fmt(t.updated_at)}</span>
                  </div>
                  <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.className}`}>{meta.label}</span>
                </button>
              );
            })}
          </div>

          {/* Conversation */}
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex flex-col">
            {selected ? (
              <>
                <div className="px-4 py-3 border-b border-background-200/70 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{selected.subject}</p>
                    <p className="text-xs text-foreground-500 truncate">{CATEGORIES.find((c) => c.value === selected.category)?.label || selected.category}</p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${(ZIFEL_SUPPORT_STATUS[selected.status] || ZIFEL_SUPPORT_STATUS.open).className}`}>
                    {(ZIFEL_SUPPORT_STATUS[selected.status] || ZIFEL_SUPPORT_STATUS.open).label}
                  </span>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-background-50/50" style={{ minHeight: '280px', maxHeight: '420px' }}>
                  {thread.map((m) => {
                    const isMe = m.sender_type === 'merchant';
                    return (
                      <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[78%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-primary-500 text-background-50 rounded-br-sm' : 'bg-background-100 text-foreground-800 rounded-bl-sm border border-background-200/70'}`}>
                            {m.message}
                          </div>
                          {m.attachment_url && (
                            <a href={m.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-1.5 block w-40 rounded-lg overflow-hidden border border-background-200/70 cursor-pointer">
                              <img src={m.attachment_url} alt="Pièce jointe" className="w-full h-auto object-cover" />
                            </a>
                          )}
                          <span className="text-[11px] text-foreground-400 mt-1 px-1">
                            {isMe ? (user?.name || 'Vous') : 'Équipe Zifek'} · {fmt(m.created_at)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef}></div>
                </div>

                <div className="px-4 py-3 border-t border-background-200/70">
                  {replyAttach && (
                    <div className="flex items-center gap-2 mb-2">
                      <div className="relative">
                        <img src={replyAttach.preview} alt="Aperçu" className="w-16 h-16 rounded-lg object-cover border border-background-200/70" />
                        <button onClick={() => setReplyAttach(null)} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-foreground-900 text-background-50 flex items-center justify-center cursor-pointer">
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                      <span className="text-xs text-foreground-400">Capture jointe</span>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <input ref={replyFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setReplyAttach({ file, preview: URL.createObjectURL(file) });
                      if (replyFileRef.current) replyFileRef.current.value = '';
                    }} />
                    <button type="button" onClick={() => replyFileRef.current?.click()} title="Joindre une capture d'écran"
                      className="h-10 w-10 flex-shrink-0 rounded-lg bg-background-100 text-foreground-500 hover:text-foreground-800 hover:bg-background-200/70 flex items-center justify-center transition-colors cursor-pointer">
                      <i className="ri-image-add-line"></i>
                    </button>
                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={1000}
                      placeholder="Écrire un message… (Entrée pour envoyer)"
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                      className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 focus:outline-none focus:border-primary-300 resize-none" />
                    <button onClick={sendReply} disabled={sending || (!text.trim() && !replyAttach)}
                      className="h-10 px-4 bg-primary-500 text-background-50 rounded-lg text-sm font-medium cursor-pointer hover:bg-primary-600 disabled:opacity-50 transition-colors flex items-center gap-1.5 whitespace-nowrap">
                      {sending ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-2-line"></i>}
                      <span className="hidden sm:inline">Envoyer</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-20 text-sm text-foreground-400">Sélectionnez une demande.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}